import { fileMatchesAccept } from "@sembark-travel/ui-base"
import {
	useXHR,
	type IListResponse,
	type XHRInstance,
} from "@sembark-travel/xhr"
import useSWR, { type SWRConfiguration } from "swr"
import type { TWAFlow, TWAFlowScreen } from "../Integrations/MessagingGateways"

/** What a quick send's message is. Never a template. */
export type TWAQuickSendMessageType =
	| "text"
	| "image"
	| "video"
	| "document"
	| "flow"

/** The kinds that carry a file. */
export type TWAQuickSendMediaType = Exclude<
	TWAQuickSendMessageType,
	"text" | "flow"
>

export const QUICK_SEND_MESSAGE_TYPES: Array<TWAQuickSendMessageType> = [
	"text",
	"image",
	"video",
	"document",
	"flow",
]

export const QUICK_SEND_MESSAGE_TYPE_LABELS: Record<
	TWAQuickSendMessageType,
	string
> = {
	text: "Text",
	image: "Image",
	video: "Video",
	document: "Document",
	flow: "Form",
}

export function isQuickSendMediaType(
	type: TWAQuickSendMessageType
): type is TWAQuickSendMediaType {
	return type !== "text" && type !== "flow"
}

/** Meta's cap on a Flow button's label. */
export const QUICK_SEND_FLOW_CTA_MAX_LENGTH = 20

/**
 * The files WhatsApp sends as each kind. A WebP is left out: Meta sends it as
 * a sticker, which drops its caption.
 */
export const QUICK_SEND_ACCEPT: Record<TWAQuickSendMediaType, string> = {
	image: "image/jpeg,image/png",
	video: "video/mp4,video/3gpp",
	document:
		"application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain",
}

/** At most this many messages go out in one quick send. */
export const QUICK_SEND_MAX_MESSAGES = 10

export type TWAQuickSendFile = {
	file_name: string
	mime_type: string
	size: number
	/** Streams the file to a signed-in agent. */
	url: string
}

/** The Flow a form message offers, as it stands now. */
export type TWAQuickSendFlow = {
	id: number
	name: string
	status: string
	/** Removed since the quick send was saved; a send refuses it. */
	deleted: boolean
}

export type TWAQuickSendMessage = {
	id: number
	position: number
	type: TWAQuickSendMessageType
	/** The text, a file's caption, or the text above a form's button. */
	body?: string | null
	/** The body with WhatsApp's formatting applied, escaped, as sent. */
	body_html?: string | null
	file?: TWAQuickSendFile | null
	wa_flow_id?: number | null
	flow_cta?: string | null
	flow_screen?: string | null
	flow?: TWAQuickSendFlow | null
}

export type TWAQuickSend = {
	id: number
	name: string
	messages?: Array<TWAQuickSendMessage>
	/** Sends that reached the customer, at least in part. */
	use_count: number
	last_used_at?: string | null
	created_at: string
	updated_at: string
}

/** One message row as the editor holds it. */
export type TWAQuickSendMessageFormValue = {
	/** Set on a saved message; absent on one added in the editor. */
	id?: number
	type: TWAQuickSendMessageType
	body?: string
	/** A newly picked file. */
	file?: File | null
	/** The saved file, shown until a new one replaces it. */
	savedFile?: TWAQuickSendFile | null
	/**
	 * The kind the saved file was saved as. A saved image is no use to a row
	 * that now says it is a video, so the row asks for a new file.
	 */
	savedType?: TWAQuickSendMessageType
	/** A form's Flow, as its picker holds it. */
	flow?: Pick<TWAFlow, "id" | "name" | "status"> | null
	/** A form's button label. */
	flowCta?: string
	/** The screen a form opens on, as its picker holds it. */
	flowScreen?: TWAFlowScreen | null
	/** The editor's row key, which also names the new file's form field. */
	__id?: number
}

export type TWAQuickSendFormValues = {
	name: string
	messages: Array<TWAQuickSendMessageFormValue>
}

/**
 * The editor's values as the server takes them: the messages as one JSON
 * document, and each new file under a field its row names, so a file stays
 * with its row whatever the order.
 */
export function buildQuickSendSubmissionData(
	values: TWAQuickSendFormValues
): FormData {
	const data = new FormData()
	data.append("name", values.name.trim())

	const messages = values.messages.map((message, index) => {
		const file =
			isQuickSendMediaType(message.type) && message.file ? message.file : null
		const fileKey = file ? `file_${message.__id ?? index}` : undefined

		if (file && fileKey) {
			data.append(fileKey, file)
		}

		return {
			...(message.id ? { id: message.id } : {}),
			type: message.type,
			body: message.body?.trim() || null,
			...(fileKey ? { file_key: fileKey } : {}),
			...(message.type === "flow"
				? {
						wa_flow_id: message.flow?.id ?? null,
						flow_cta: message.flowCta?.trim() || null,
						flow_screen: message.flowScreen?.id ?? null,
					}
				: {}),
		}
	})

	data.append("messages", JSON.stringify(messages))

	return data
}

/** A saved quick send in the editor's shape. */
export function quickSendFormValues(
	quickSend?: TWAQuickSend | null
): TWAQuickSendFormValues {
	return {
		name: quickSend?.name ?? "",
		messages: (quickSend?.messages ?? []).map((message) => ({
			id: message.id,
			type: message.type,
			body: message.body ?? "",
			file: null,
			savedFile: message.file ?? null,
			savedType: message.type,
			flow: message.flow
				? {
						id: message.flow.id,
						name: message.flow.name,
						status: message.flow.status,
					}
				: null,
			flowCta: message.flow_cta ?? "",
			flowScreen: message.flow_screen
				? { id: message.flow_screen, title: message.flow_screen }
				: null,
		})),
	}
}

/**
 * The kind of message a file goes out as in a quick send, or null when it is
 * none WhatsApp sends here — a WebP, say, which Meta sends as a sticker.
 */
export function quickSendTypeForFile(file: File): TWAQuickSendMediaType | null {
	const types = Object.keys(QUICK_SEND_ACCEPT) as Array<TWAQuickSendMediaType>

	return (
		types.find((type) => fileMatchesAccept(file, QUICK_SEND_ACCEPT[type])) ??
		null
	)
}

/** The saved file, if it still suits the kind the row now says it is. */
export function usableSavedFile(
	message: Pick<
		TWAQuickSendMessageFormValue,
		"type" | "savedFile" | "savedType"
	>
): TWAQuickSendFile | null {
	return message.savedFile && message.savedType === message.type
		? message.savedFile
		: null
}

export type TWAQuickSendRunStatus = "sending" | "completed" | "failed"

/**
 * How far one sending of a quick send has got. Each message after the first
 * goes out once WhatsApp reports the one before it sent, so a run stays
 * `sending` for a while. It stops at the first message that fails, and
 * `failed_at` names the gap an agent has to fill by hand.
 */
export type TWAQuickSendResult = {
	message: string
	data: {
		id: number
		status: TWAQuickSendRunStatus
		total: number
		sent: number
		wa_message_ids: Array<number>
		failed_at: number | null
		error: string | null
		/** The agent who pressed Send; every message goes out as them. */
		created_by: { id: number; name: string } | null
		created_at: string
	}
}

/** How often a run still sending is read again. */
const RUN_POLL_INTERVAL_MS = 2000

/**
 * A run, read again every few seconds for as long as it is still sending,
 * and left alone once it has finished.
 */
export function useWAQuickSendRun(initial: TWAQuickSendResult | null) {
	const xhr = useXHR()
	const id = initial?.data.id
	return useSWR<TWAQuickSendResult>(
		id ? `/wa-quick-sends/runs/${id}` : null,
		() =>
			xhr
				.get<TWAQuickSendResult>(`/wa-quick-sends/runs/${id}`)
				.then((resp) => resp.data),
		{
			fallbackData: initial ?? undefined,
			revalidateOnMount: initial?.data.status === "sending",
			revalidateOnFocus: false,
			refreshInterval: (latest) =>
				latest?.data.status === "sending" ? RUN_POLL_INTERVAL_MS : 0,
		}
	)
}

export function useFetchWAQuickSend(
	id: number | string | null | undefined,
	config?: SWRConfiguration
) {
	const xhr = useXHR()
	return useSWR<TWAQuickSend>(
		id ? `/wa-quick-sends/${id}` : null,
		() =>
			xhr
				.get<{ data: TWAQuickSend }>(`/wa-quick-sends/${id}`)
				.then((resp) => resp.data.data),
		config
	)
}

/**
 * Quick sends matching a search, for the composer's picker. Fetched only
 * while the picker is open.
 */
export function useSearchWAQuickSends(enabled: boolean, query: string) {
	const xhr = useXHR()
	const key = enabled ? `/wa-quick-sends?q=${query}` : null
	return useSWR<Array<TWAQuickSend>>(key, () =>
		xhr
			.get<IListResponse<TWAQuickSend>>("/wa-quick-sends", {
				// The ones agents reach for most come first.
				params: { q: query || undefined, sort: "most_used", limit: 50 },
			})
			.then((resp) => resp.data.data)
	)
}

export function WAQuickSendsXHR(xhr: XHRInstance) {
	return {
		async create(values: TWAQuickSendFormValues): Promise<TWAQuickSend> {
			return xhr
				.post<{
					data: TWAQuickSend
				}>("/wa-quick-sends", buildQuickSendSubmissionData(values))
				.then((resp) => resp.data.data)
		},
		async update(
			id: number,
			values: TWAQuickSendFormValues
		): Promise<TWAQuickSend> {
			return xhr
				.put<{
					data: TWAQuickSend
				}>(`/wa-quick-sends/${id}`, buildQuickSendSubmissionData(values))
				.then((resp) => resp.data.data)
		},
		async remove(id: number): Promise<void> {
			return xhr.delete(`/wa-quick-sends/${id}`).then(() => undefined)
		},
	}
}
